import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Loader2, Heart, LogIn, UserPlus, KeyRound, ArrowLeft, 
  CheckCircle, Shield, Users, Home, Phone, Mail
} from 'lucide-react';
import { getSecurityQuestions, requestPasswordReset, verifyAndResetPassword, verify2FA } from '@/lib/api';
import LanguageSelector from '@/components/LanguageSelector';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: "patient", label: "Patient", description: "I am receiving hospice care" },
  { value: "caregiver", label: "Family Caregiver", description: "I am caring for a loved one" },
  { value: "staff", label: "Staff Member", description: "I work for Goodwin Hospice" },
  { value: "admin", label: "Administrator", description: "I am a Goodwin Hospice administrator" },
];

export default function LoginPage() {
  const { login, register, complete2FA, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [resetStep, setResetStep] = useState(0);
  const [securityQuestions, setSecurityQuestions] = useState([]);
  const [show2FA, setShow2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState("");
  const [pendingUser, setPendingUser] = useState(null);
  
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({ 
    email: "", password: "", name: "", role: "caregiver",
    securityQuestion: "", securityAnswer: ""
  });
  const [resetData, setResetData] = useState({
    email: "", securityQuestion: "", securityAnswer: "",
    newPassword: "", confirmPassword: ""
  });

  useEffect(() => {
    // Redirect if already logged in
    if (user) {
      navigate('/dashboard');
    }
    loadSecurityQuestions();
  }, [user, navigate]);

  const loadSecurityQuestions = async () => {
    try {
      const data = await getSecurityQuestions();
      setSecurityQuestions(data.questions || []);
    } catch (error) {
      console.error("Failed to load security questions:", error);
    }
  };

  const fetchDashboardRedirect = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/rbac/dashboard-redirect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        return data.route || '/dashboard';
      }
    } catch (error) {
      console.error("Failed to fetch dashboard redirect:", error);
    }
    return '/dashboard';
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await login(loginData.email, loginData.password);
      
      if (response.requires_2fa) {
        setPendingUser(response);
        setShow2FA(true);
        toast.info("Please enter your 2FA code");
      } else {
        toast.success("Welcome back!");
        const redirectRoute = await fetchDashboardRedirect(response.token);
        navigate(redirectRoute);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FAVerify = async () => {
    if (twoFACode.length !== 6 && twoFACode.length !== 8) {
      toast.error("Please enter a valid code");
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await verify2FA(twoFACode);
      localStorage.setItem("hospice_token", response.token);
      complete2FA(pendingUser);
      toast.success("Welcome back!");
      const redirectRoute = await fetchDashboardRedirect(response.token);
      navigate(redirectRoute);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setIsLoading(true);
    try {
      const response = await register(
        registerData.email, registerData.password, registerData.name, 
        registerData.role, registerData.securityQuestion || null,
        registerData.securityAnswer || null
      );
      toast.success("Account created successfully!");
      const redirectRoute = await fetchDashboardRedirect(response.token);
      navigate(redirectRoute);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await requestPasswordReset(resetData.email);
      if (response.has_security_question) {
        setResetData(prev => ({ ...prev, securityQuestion: response.security_question }));
        setResetStep(1);
      } else {
        toast.error("No security question set for this account. Please contact support.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate password reset");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyReset = async (e) => {
    e.preventDefault();
    if (resetData.newPassword !== resetData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (resetData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setIsLoading(true);
    try {
      await verifyAndResetPassword(resetData.email, resetData.securityAnswer, resetData.newPassword);
      setResetStep(2);
      toast.success("Password reset successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  const resetToLogin = () => {
    setActiveTab("login");
    setResetStep(0);
    setResetData({ email: "", securityQuestion: "", securityAnswer: "", newPassword: "", confirmPassword: "" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex flex-col" data-testid="login-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-lg">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Goodwin Cares</h1>
              <p className="text-xs text-slate-500">Compassionate Hospice Care</p>
            </div>
          </Link>
          <Link to="/">
            <Button variant="outline" size="sm">
              <Home className="w-4 h-4 mr-2" />
              {t("common.back", "Back to Home")}
            </Button>
          </Link>
          <LanguageSelector variant="outline" />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm" data-testid="login-card">
            <CardHeader className="text-center pb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900">{t("auth.welcomeBack")}</CardTitle>
              <CardDescription className="text-slate-600 font-medium">
                {t("auth.signInDescription", "Sign in to access your care portal, messages, and health resources")}
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-3 mb-6">
                  <TabsTrigger value="login" className="text-sm" data-testid="login-tab">
                    <LogIn className="w-4 h-4 mr-1.5" />
                    {t("nav.signIn")}
                  </TabsTrigger>
                  <TabsTrigger value="register" className="text-sm" data-testid="register-tab">
                    <UserPlus className="w-4 h-4 mr-1.5" />
                    {t("auth.signUp")}
                  </TabsTrigger>
                  <TabsTrigger value="reset" className="text-sm" data-testid="reset-tab">
                    <KeyRound className="w-4 h-4 mr-1.5" />
                    {t("auth.reset", "Reset")}
                  </TabsTrigger>
                </TabsList>

                {/* Login Tab */}
                <TabsContent value="login">
                  {!show2FA ? (
                    <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                      <div className="space-y-2">
                        <Label htmlFor="login-email" className="text-slate-700 font-medium">{t("auth.email")}</Label>
                        <Input
                          id="login-email"
                          type="email"
                          placeholder={t("auth.emailPlaceholder", "your@email.com")}
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          required
                          className="h-11 border-slate-300 focus:border-teal-500 focus:ring-teal-500"
                          data-testid="login-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="login-password" className="text-slate-700 font-medium">{t("auth.password")}</Label>
                        <Input
                          id="login-password"
                          type="password"
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          required
                          className="h-11 border-slate-300 focus:border-teal-500 focus:ring-teal-500"
                          data-testid="login-password-input"
                        />
                      </div>
                      <Button 
                        type="submit" 
                        className="w-full h-11 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-medium shadow-lg" 
                        disabled={isLoading}
                        data-testid="login-submit-btn"
                      >
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
                        {t("auth.signInToPortal", "Sign In to Portal")}
                      </Button>
                      <button
                        type="button"
                        onClick={() => setActiveTab("reset")}
                        className="w-full text-sm text-teal-600 hover:text-teal-700 hover:underline mt-2"
                      >
                        {t("auth.forgotPassword")}
                      </button>
                      
                      {/* Quick Demo Access */}
                      <div className="mt-6 pt-4 border-t border-slate-200">
                        <p className="text-xs text-slate-600 text-center mb-3 font-medium">Quick access for demo:</p>
                        <div className="grid grid-cols-2 gap-2">
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            className="text-xs text-slate-700 border-slate-300 font-medium"
                            onClick={() => setLoginData({ email: 'admin_test@goodwincares.com', password: 'Test1234!' })}
                          >
                            Admin
                          </Button>
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            className="text-xs text-slate-700 border-slate-300 font-medium"
                            onClick={() => setLoginData({ email: 'staff_test@goodwincares.com', password: 'Test1234!' })}
                          >
                            Staff
                          </Button>
                        </div>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-4" data-testid="2fa-form">
                      <div className="text-center">
                        <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-3">
                          <Shield className="w-7 h-7 text-teal-600" />
                        </div>
                        <p className="text-sm text-slate-600">
                          Enter the 6-digit code from your authenticator app
                        </p>
                      </div>
                      <Input
                        type="text"
                        inputMode="numeric"
                        maxLength={8}
                        placeholder="000000"
                        value={twoFACode}
                        onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                        className="text-center text-2xl tracking-[0.3em] font-mono h-14"
                        autoFocus
                        data-testid="2fa-code-input"
                      />
                      <Button 
                        className="w-full h-11 bg-gradient-to-r from-teal-600 to-cyan-600" 
                        onClick={handle2FAVerify}
                        disabled={isLoading || (twoFACode.length !== 6 && twoFACode.length !== 8)}
                        data-testid="2fa-verify-btn"
                      >
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                        Verify Code
                      </Button>
                      <button
                        type="button"
                        onClick={() => { setShow2FA(false); setTwoFACode(""); setPendingUser(null); }}
                        className="w-full text-sm text-slate-500 hover:text-slate-700 flex items-center justify-center"
                      >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back to login
                      </button>
                    </div>
                  )}
                </TabsContent>

                {/* Register Tab */}
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                    <div className="space-y-2">
                      <Label htmlFor="register-name" className="text-slate-700 font-medium">Full Name</Label>
                      <Input
                        id="register-name"
                        placeholder="Your full name"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        required
                        className="h-11 border-slate-300"
                        data-testid="register-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email" className="text-slate-700 font-medium">Email Address</Label>
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="your@email.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        required
                        className="h-11 border-slate-300"
                        data-testid="register-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password" className="text-slate-700 font-medium">Password</Label>
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="Minimum 6 characters"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                        minLength={6}
                        className="h-11 border-slate-300"
                        data-testid="register-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium flex items-center gap-2">
                        <Users className="w-4 h-4 text-teal-600" />
                        I am a...
                      </Label>
                      <Select
                        value={registerData.role}
                        onValueChange={(value) => setRegisterData({ ...registerData, role: value })}
                      >
                        <SelectTrigger className="h-11" data-testid="register-role-select">
                          <SelectValue placeholder="Select your role" />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLE_OPTIONS.map((role) => (
                            <SelectItem key={role.value} value={role.value}>
                              <div className="flex flex-col">
                                <span className="font-medium">{role.label}</span>
                                <span className="text-xs text-slate-500">{role.description}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full h-11 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700" 
                      disabled={isLoading}
                      data-testid="register-submit-btn"
                    >
                      {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                      Create Account
                    </Button>
                  </form>
                </TabsContent>

                {/* Reset Tab */}
                <TabsContent value="reset">
                  {resetStep === 0 && (
                    <form onSubmit={handleRequestReset} className="space-y-4">
                      <p className="text-sm text-slate-600">
                        {t("auth.resetDescription", "Enter your email address and we will help you reset your password.")}
                      </p>
                      <div className="space-y-2">
                        <Label htmlFor="reset-email" className="text-slate-700 font-medium">{t("auth.email")}</Label>
                        <Input
                          id="reset-email"
                          type="email"
                          placeholder="your@email.com"
                          value={resetData.email}
                          onChange={(e) => setResetData({ ...resetData, email: e.target.value })}
                          required
                          className="h-11 border-slate-300"
                        />
                      </div>
                      <Button type="submit" className="w-full h-11 bg-gradient-to-r from-teal-600 to-cyan-600" disabled={isLoading}>
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
                        Continue
                      </Button>
                    </form>
                  )}

                  {resetStep === 1 && (
                    <form onSubmit={handleVerifyReset} className="space-y-4">
                      <button
                        type="button"
                        onClick={() => setResetStep(0)}
                        className="flex items-center text-sm text-slate-500 hover:text-slate-700"
                      >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back
                      </button>
                      <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                        <p className="text-sm font-medium text-teal-700">{resetData.securityQuestion}</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="reset-answer" className="text-slate-700 font-medium">Your Answer</Label>
                        <Input
                          id="reset-answer"
                          placeholder="Enter your answer"
                          value={resetData.securityAnswer}
                          onChange={(e) => setResetData({ ...resetData, securityAnswer: e.target.value })}
                          required
                          className="h-11 border-slate-300"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-password" className="text-slate-700 font-medium">New Password</Label>
                        <Input
                          id="new-password"
                          type="password"
                          placeholder="••••••••"
                          value={resetData.newPassword}
                          onChange={(e) => setResetData({ ...resetData, newPassword: e.target.value })}
                          required
                          minLength={6}
                          className="h-11 border-slate-300"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirm-password" className="text-slate-700 font-medium">Confirm Password</Label>
                        <Input
                          id="confirm-password"
                          type="password"
                          placeholder="••••••••"
                          value={resetData.confirmPassword}
                          onChange={(e) => setResetData({ ...resetData, confirmPassword: e.target.value })}
                          required
                          className="h-11 border-slate-300"
                        />
                      </div>
                      <Button type="submit" className="w-full h-11 bg-gradient-to-r from-teal-600 to-cyan-600" disabled={isLoading}>
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
                        Reset Password
                      </Button>
                    </form>
                  )}

                  {resetStep === 2 && (
                    <div className="text-center space-y-4 py-6">
                      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-800">Password Reset Successfully!</h3>
                      <p className="text-sm text-slate-600">
                        You can now sign in with your new password.
                      </p>
                      <Button onClick={resetToLogin} className="bg-gradient-to-r from-teal-600 to-cyan-600">
                        <LogIn className="w-4 h-4 mr-2" />
                        Sign In Now
                      </Button>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Need help accessing your account?
            </p>
            <div className="flex items-center justify-center gap-4 mt-2">
              <a href="tel:703-578-7108" className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1">
                <Phone className="w-4 h-4" />
                703-578-7108
              </a>
              <span className="text-slate-300">|</span>
              <a href="mailto:hospice@goodwinliving.org" className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1">
                <Mail className="w-4 h-4" />
                hospice@goodwinliving.org
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white/80 backdrop-blur-sm border-t border-slate-200 py-4">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-slate-500">
            © 2026 Goodwin Cares. All rights reserved. | 
            <a href="/privacy" className="text-teal-600 hover:underline ml-1">Privacy Policy</a> | 
            <a href="/terms" className="text-teal-600 hover:underline ml-1">Terms of Service</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
